import { useState, useMemo, useCallback, ReactNode } from 'react';
import { startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, startOfWeek, endOfWeek } from 'date-fns';
import { ScheduleContextObject } from './ScheduleContextObject';
import { ViewMode, CalendarItem, DateRange } from '../types';
import { useJobs } from '../../jobs/hooks/useJobs';
import { useQuotes } from '../../quotes/hooks/useQuotes';
import { Job } from '../../jobs/types';
import { Quote, QUOTE_STATUS } from '@cleanpro/common-ui';

interface ScheduleContextProps {
  children: ReactNode;
}

export function ScheduleContext({ children }: ScheduleContextProps) {
  const { jobs } = useJobs();
  const { quotes } = useQuotes();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [showJobs, setShowJobs] = useState(true);
  const [showQuotes, setShowQuotes] = useState(true);

  // Calculate date ranges based on view mode
  const dateRange = useMemo((): DateRange => {
    switch (viewMode) {
      case 'month': {
        // For month view, start from Sunday of the week containing the first day of the month
        // and end on Saturday of the week containing the last day of the month
        const monthStart = startOfMonth(currentDate);
        const monthEnd = endOfMonth(currentDate);
        return {
          start: startOfWeek(monthStart, { weekStartsOn: 0 }), // Sunday = 0
          end: endOfWeek(monthEnd, { weekStartsOn: 0 }),
        };
      }
      case 'week': {
        return {
          start: startOfWeek(currentDate, { weekStartsOn: 0 }),
          end: endOfWeek(currentDate, { weekStartsOn: 0 }),
        };
      }
      case 'day': {
        return {
          start: currentDate,
          end: currentDate,
        };
      }
      default: {
        return {
          start: startOfWeek(currentDate, { weekStartsOn: 0 }),
          end: endOfWeek(currentDate, { weekStartsOn: 0 }),
        };
      }
    }
  }, [currentDate, viewMode]);

  const calendarDays = useMemo(() => eachDayOfInterval(dateRange), [dateRange]);

  // Combine jobs and quotes for a given date
  const getItemsForDate = useMemo(() => (date: Date): CalendarItem[] => {
    let items: CalendarItem[] = [];
    if (showJobs) {
      items = items.concat(
        jobs
          .filter((job: Job) => {
            // Use date_time for jobs (scheduled date/time)
            return job.date_time && isSameDay(new Date(job.date_time), date) && job.status !== 'cancelled';
          })
          .map((job: Job) => ({ ...job, _type: 'job' as const }))
      );
    }
    if (showQuotes) {
      items = items.concat(
        quotes
          .filter((quote: Quote) => {
            // Only show quotes that are awaiting_response or pending_assignment
            const isValidStatus = quote.status === QUOTE_STATUS.AWAITING_RESPONSE || 
                                 quote.status === QUOTE_STATUS.PENDING_ASSIGNMENT;
            
            // Use requestedDateTime for quotes (customer's requested date/time)
            return isValidStatus && 
                   quote.requestedDateTime && 
                   isSameDay(new Date(quote.requestedDateTime), date);
          })
          .map((quote: Quote) => ({ ...quote, _type: 'quote' as const }))
      );
    }
    return items.sort((a, b) => {
      // Extract time from date_time or requestedDateTime
      const getTime = (item: CalendarItem) => {
        if ('date_time' in item && item.date_time) {
          return new Date(item.date_time).toTimeString();
        }
        if ('requestedDateTime' in item && item.requestedDateTime) {
          return new Date(item.requestedDateTime).toTimeString();
        }
        return '';
      };
      
      const aTime = getTime(a);
      const bTime = getTime(b);
      
      if (aTime && bTime) {
        return aTime.localeCompare(bTime);
      }
      if (aTime) return -1;
      if (bTime) return 1;
      
      // fallback: sort by createdAt
      const aCreated = new Date('createdAt' in a && a.createdAt ? a.createdAt : 0);
      const bCreated = new Date('createdAt' in b && b.createdAt ? b.createdAt : 0);
      return aCreated.getTime() - bCreated.getTime();
    });
  }, [jobs, quotes, showJobs, showQuotes]);

  const navigateDate = useCallback((direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    const increment = direction === 'next' ? 1 : -1;
    
    switch (viewMode) {
      case 'month':
        newDate.setMonth(newDate.getMonth() + increment);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (7 * increment));
        break;
      case 'day':
        newDate.setDate(newDate.getDate() + increment);
        break;
      default:
        newDate.setDate(newDate.getDate() + (7 * increment));
        break;
    }
    setCurrentDate(newDate);
  }, [currentDate, viewMode]);

  const goToToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const value = useMemo(() => ({
    // State
    currentDate,
    viewMode,
    showJobs,
    showQuotes,
    
    // Actions
    setCurrentDate,
    setViewMode,
    setShowJobs,
    setShowQuotes,
    navigateDate,
    goToToday,
    
    // Computed values
    dateRange,
    calendarDays,
    getItemsForDate,
  }), [
    currentDate,
    viewMode,
    showJobs,
    showQuotes,
    navigateDate,
    goToToday,
    dateRange,
    calendarDays,
    getItemsForDate,
  ]);

  return (
    <ScheduleContextObject.Provider value={value}>
      {children}
    </ScheduleContextObject.Provider>
  );
}